import { SectionType } from '../types';

// Map static form step IDs to dynamic backend section types
export const SECTION_MAPPING: Record<string, SectionType> = {
  'contest-info': 'APPLICATION_INFORMATION',
  'basic': 'PROJECT_INFORMATION', 
  'content': 'PROJECT_DESCRIPTION',
  'team': 'PROJECT_TEAM',
  'budget': 'PROJECT_RESOURCES',
  'publications': 'PROJECT_ATTACHMENTS',
  'review': 'PROJECT_SUMMARY'
};

// Reverse mapping for converting backend sections to step IDs
export const REVERSE_SECTION_MAPPING: Record<SectionType, string> = {
  'APPLICATION_INFORMATION': 'contest-info',
  'PROJECT_INFORMATION': 'basic',
  'PROJECT_DESCRIPTION': 'content', 
  'PROJECT_TEAM': 'team',
  'PROJECT_RESOURCES': 'budget',
  'PROJECT_ATTACHMENTS': 'publications',
  'PROJECT_SUMMARY': 'review'
};

export const getStaticStepId = (sectionType: SectionType): string => {
  return REVERSE_SECTION_MAPPING[sectionType] || '';
};

export const getDynamicSectionType = (stepId: string): SectionType => {
  return SECTION_MAPPING[stepId] || 'APPLICATION_INFORMATION';
};

export const getSectionTitle = (stepId: string): string => {
  const titles: Record<string, string> = {
    'contest-info': 'Información de Concurso',
    'basic': 'Antecedentes', 
    'content': 'Formulación',
    'team': 'Equipo de Trabajo',
    'budget': 'Recursos',
    'publications': 'Publicaciones y Anexos',
    'review': 'Revisión y Envío'
  };
  return titles[stepId] || '';
};

// Helper to determine which component to render for each section type
export const getComponentName = (sectionType: SectionType): string => {
  const componentMapping: Record<SectionType, string> = {
    'APPLICATION_INFORMATION': 'ContestInformation',
    'PROJECT_INFORMATION': 'BasicInformation',
    'PROJECT_DESCRIPTION': 'ProjectContent', 
    'PROJECT_TEAM': 'TeamInformation',
    'PROJECT_RESOURCES': 'BudgetSection',
    'PROJECT_ATTACHMENTS': 'PublicationsSection',
    'PROJECT_SUMMARY': 'ReviewSubmission'
  };
  return componentMapping[sectionType] || 'ContestInformation';
};

// Extract section-specific data from complete form data
export const extractSectionData = (stepId: string, fullFormData: any): any => {
  const sectionFields: Record<string, string[]> = {
    'contest-info': [], // Static info, no form fields
    'basic': [
      'projectTitle', 'interdisciplinaryProject', 'transdisciplinaryProject', 
      'genderApproachProject', 'genderApproachOptions', 'openScience', 'rcer', 'keywords', 
      'evaluationArea', 'oecdArea', 'oecdSubarea', 'oecdDiscipline', 'sdgGoals', 'executionRegions'
    ],
    'content': [
      'executiveSummary', 'projectDocument'
    ],
    'team': ['principalInvestigator', 'coInvestigators'],
    'budget': ['budget'],
    'publications': [
      'ipCvData', 'suggestedReviewers', 'hasConflictOfInterest', 'conflictOfInterestList',
      'principalInvestigatorDoiFile', 'coInvestigatorDoiFile', 'institutionCommitmentLetters'
    ],
    'review': [
      'finalReportsUC', 'finalReportsANID', 'duplicateFunding', 'otherIncompatibilities', 
      'otherIncompatibilitiesDetails', 'noIncompatibilities', 'informationDeclaration', 'status', 'submittedAt'
    ]
  };

  const fieldsForSection = sectionFields[stepId] || [];
  const sectionData: any = {};

  // ONLY include fields that are explicitly defined for this section
  fieldsForSection.forEach(field => {
    if (fullFormData && fullFormData[field] !== undefined) {
      let value = fullFormData[field];

      // Special handling: Trim keywords at save/submit time
      if (field === 'keywords' && typeof value === 'string') {
        // Split by semicolon, trim each keyword, and rejoin
        value = value
          .split(';')
          .map(k => k.trim())
          .filter(k => k !== '')
          .join(';');
      }

      sectionData[field] = value;
    }
  });

  return sectionData;
};

// Build complete form state by merging all section data from backend
export const buildCompleteFormState = (sectionAnswers: Record<string, any>): any => {
  const completeFormState: any = {};

  // Merge all section data into a single form state
  Object.keys(sectionAnswers).forEach(sectionType => {
    const sectionData = sectionAnswers[sectionType] || {};
    Object.keys(sectionData).forEach(field => {
      completeFormState[field] = sectionData[field];
    });
  });

  return completeFormState;
};